<template>
    <div v-if="hasStripeIntegration">
        <h1 class="text-lg font-medium tracking-tight text-iron-700">
            {{ $t("event_types.paymennts.title") }}
        </h1>
        <p class="text-sm text-iron-500">
            {{ $t("event_types.paymennts.description") }}
        </p>
        <div class="mt-5 space-x-3 font-medium text-iron-700 flex items-center">
            <Switch id="enabled" v-model:checked="enabled" />
            <Label for="enabled">
                {{ $t("event_types.paymennts.enabled") }}</Label
            >
        </div>
        <div v-if="enabled">
            <div class="mt-4 gap-4 flex w-full">
                <div class="gap-1 grid w-full items-center">
                    <div class="w-full">
                        <Label for="currency">{{
                            $t("event_types.paymennts.currency")
                        }}</Label>

                        <Select
                            :model-value="currency.value"
                            @update:model-value="updateCurrency"
                        >
                            <SelectTrigger class="w-[180px]">
                                <SelectValue placeholder="Select a fruit" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectGroup>
                                    <SelectItem
                                        v-for="c of currencies"
                                        :key="c.value"
                                        :value="c.value"
                                    >
                                        {{ c.label }}
                                    </SelectItem>
                                </SelectGroup>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div class="grid w-full items-center">
                    <Label for="price">{{
                        $t("event_types.paymennts.price")
                    }}</Label>
                    <Input
                        id="price"
                        v-model="price"
                        type="number"
                        :min="currency?.min"
                        placeholder=""
                    />
                </div>
            </div>

            <div class="mt-4 flex justify-end">
                <Button v-if="hasChanges" @click="savePayment">{{
                    $t("save")
                }}</Button>
            </div>
        </div>
    </div>
    <div v-else>
        <Link href="/integrations">
            <PromoBanner>
                <template #title>{{
                    $t("event_types.paymennts.banner.title")
                }}</template>
                <template #description>{{
                    $t("event_types.paymennts.banner.description")
                }}</template>
                <template #icon>
                    <CircleDollarSign :size="20" class="text-[#E92F34]" />
                </template>
            </PromoBanner>
        </Link>
    </div>
</template>

<script lang="ts" setup>
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { EventType } from "../types/EventType";
import { Switch } from "@/components/ui/switch";
import { ref, watch } from "vue";
import axios from "axios";
import route from "ziggy-js";
import PromoBanner from "@/Pages/Zaptime/App/Atomic/PromoBanner.vue";
import { Link } from "@inertiajs/vue3";
import { CircleDollarSign } from "lucide-vue-next";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
const currencies = [
    {
        value: "USD",
        label: "USD",
        min: 5,
    },
    {
        value: "EUR",
        label: "EUR",
        min: 5,
    },
    {
        value: "CZK",
        label: "CZK",
        min: 50,
    },
];

const props = defineProps<{
    eventType: EventType;
    hasStripeIntegration: boolean;
}>();

const enabled = ref(props.eventType.stripe_config?.enabled ?? false);

const hasChanges = ref(false);

watch(enabled, (value) => {
    axios.post(
        route("app.event-types.payments.toggle", {
            eventType: props.eventType.id,
        }),
        { enabled: value },
    );
});

function updateCurrency(newCurrency: any) {
    const foundCurrency = currencies.find((c) => c.value === newCurrency);

    if (foundCurrency) {
        currency.value = foundCurrency;
    }
}

const currency = ref(
    props.eventType.stripe_config
        ? (currencies.find(
              (c) => c.value === props.eventType.stripe_config.currency,
          ) ?? currencies[0])
        : currencies[0],
);

const price = ref(
    props.eventType.stripe_config
        ? props.eventType.stripe_config.price / 100
        : 10,
);

watch([price, currency], () => {
    hasChanges.value = true;
});

async function savePayment() {
    if (currency.value === undefined) {
        return;
    }

    await axios.post(
        route("app.event-types.payments.store", {
            eventType: props.eventType.id,
        }),
        {
            enabled: enabled.value,
            currency: currency.value.value,
            price: price.value,
        },
    );

    window.location.reload();

    hasChanges.value = false;
}
</script>
